import { useEffect, useRef } from "react";
import customError from "custom-error";
import { Parser } from "json2csv";
import _ from "underscore";
import moment from "moment";
import numeral from 'numeral';
import { useLocation } from "react-router";

export const AuthError = customError("AuthError");

export const useInterval = (callback, delay) => {
  const savedCallback = useRef(callback)

  // Remember the latest callback if it changes.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return
    }

    const id = setInterval(() => savedCallback.current(), delay)

    return () => clearInterval(id)
  }, [delay])
}

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const getCommentsSubmitMessage = (privateStatus, userView, profileType) => {
  if (privateStatus) {
    return "This comment will only be visible to you"
  }
  else {
    if (userView) {
      return "This comment will be visible to all users"
    }
    else {
      return profileType === "admin" ? "This comment will only be visible to admins" : "This comment will  be visible to all users"
    }
  }
};

export const decodeHtmlEntities = (value) => {
  if (typeof value !== "string") return value;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

export const convertJSONToCSV = (data, fields) => {
  try {
    const opts = {
      eol: '\r\n' // Specify CRLF line endings
    };
    const parser = new Parser(opts);
    const csv = parser.parse(data, { fields });
    return csv;
  } catch (err) {
    return null;
  }
};

export const getAllowedFields = (userType, fields, userEditTags) => {
  const pathname = window.location.pathname
  const meRoute = pathname?.includes("/me/")

  let finalFields = []
  if (userType === "admin") {
    if (userEditTags && userEditTags?.length) {
      finalFields = fields?.filter((item) => {
        const finalStatus = _.intersection(meRoute ? item?.me_edit_tag : item?.edit_tag, userEditTags)?.length > 0
        return finalStatus
      })
    }
    else {
      finalFields = fields
    }
  }
  else if (userType !== "admin") {
    finalFields = fields?.filter((item) => {
      const finalStatus = _.intersection(meRoute ? item?.me_edit_tag : item?.edit_tag, userEditTags)?.length > 0
      return finalStatus
    })
  }
  return finalFields
};

export const getAllowedViewFields = (userType, fields, userViewTags) => {
  const pathname = window.location.pathname
  const meRoute = pathname?.includes("/me/")

  let finalFields = []
  if (userType === "admin") {
    if (userViewTags && userViewTags?.length) {
      finalFields = fields?.filter((item) => {
        const finalStatus = _.intersection(meRoute ? item?.me_view_tag : item?.view_tag, userViewTags)?.length > 0
        return finalStatus
      })
    }
    else {
      finalFields = fields
    }
  }
  else if (userType !== "admin") {
    finalFields = fields?.filter((item) => {
      const finalStatus = _.intersection(meRoute ? item?.me_view_tag : item?.view_tag, userViewTags)?.length > 0
      return finalStatus
    })
  }
  return finalFields
};

export const checkStatus = (response) => {
  if (response.statusCode >= 200 && response.statusCode < 300) return response;

  if (response.statusCode == 401 && response.statusCode !== "") {
    let error = new AuthError();
    error.message = response.message;
    throw error;
  } else {
    let error = new Error();
    error.message = response.message;
    throw error;
  }
};

export const parseJSON = (response) => response.json();

export const composeAuth = (jwt) => "Bearer " + jwt;

export const getById = (data, field = "id") => {
  let byId = {};
  data.forEach((s) => {
    byId[s[field]] = s;
  });
  return byId;
};

export const getByField = (collection, property) => {
  var i = 0,
    val,
    index,
    values = [],
    result = [];
  if (collection.length > 0) {
    for (; i < collection.length; i++) {
      val = collection[i][property];
      index = values.indexOf(val);
      if (index > -1) result[index].push(collection[i]);
      else {
        values.push(val);
        result.push([collection[i]]);
      }
    }
  }
  return result;
};

export const getFlatenedSchema = (
  schema,
  data,
  filterArray,
  avoidSectionNameFilter,
  avoidStaticFields
) => {
  const isValidSchemaItem = (item) =>
    item?.type !== "multiSelectGoal" &&
    item?.type !== "multiSelectChildGoal" &&
    item?.type !== "multiSelect" &&
    item?.type !== "templateGoal" &&
    (!avoidSectionNameFilter ? item?.section_name : true) &&
    (avoidStaticFields ? !["title", "image", "banner"].includes(item?.key) : true);

  const filteredSchema = schema?.filter(isValidSchemaItem)?.map((item) => ({
    ...item,
    section_name: item?.section_name || "Goal Details",
    section_order: item?.section_order || 0,
  }));

  const result = [];

  const handlers = {
    number: (item) => ({
      ...item,
      value: data[item.key] || data[item.key] === 0 ? Number(data[item.key]) : data[item.key],
    }),
    profile: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key],
      _group_value: item?.disable_profile_group_selection ? [] : data[`_groups_${item.key}`],
    }),
    client: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key],
    }),
    vendor: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key],
    }),
    location: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key],
    }),
    income: (item) => {
      const finalValue = Array.isArray(data[item.key])
        ? data[item.key].length
          ? data[item.key].filter(Boolean)
          : []
        : data[item.key] ?? null;

      return finalValue || item.important
        ? {
          ...item,
          _parentGoalValueAssigned: !!finalValue,
          value: finalValue,
        }
        : null;
    },
    securityDeposit: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key] ?? [],
    }),
    checkList: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key],
    }),
    expense: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key],
    }),
    status: (item) => {
      const optKeys = item.options?.map((opt) => opt.key) || [];
      const value = optKeys.includes(data[item.key])
        ? data[item.key]
        : data[`_public_${item.key}`];

      return { ...item, value };
    },
    createdDetails: (item) => ({
      ...item,
      value:
        data?.created_at && data?.createdByName
          ? { created_at: data.created_at, created_by: data.createdByName }
          : "",
    }),
    default: (item) => ({
      ...item,
      value: Array.isArray(data[item.key]) ? data[item.key].filter(Boolean) : data[item.key],
    }),
  };

  filteredSchema.forEach((item) => {
    // Skip invalid schema item
    if (!item || typeof item !== "object") return;

    const type = item?.type;
    const key = item?.key;

    // Safe value extraction
    const value = key ? data?.[key] : undefined;

    const hasValue =
      item?.important ||
      type === "boolean" ||
      type === "ai" ||
      type === "number" ||
      type === "income" ||
      type === "securityDeposit" ||
      type === "checkList" ||
      type === "createdDetails" ||
      (Array.isArray(value) ? value.length > 0 : !!value);

    if (!hasValue) return;

    if (type === "checkList" && item?.hideChecklistField) {
      if (!value || (Array.isArray(value) && value.length == 0)) {
        return;
      }
    }

    // Filter out unwanted types
    if (Array.isArray(filterArray) && filterArray.includes(type)) return;

    // Find handler safely
    const handler =
      (type && handlers?.[type]) ||
      handlers?.default ||
      null;

    if (typeof handler !== "function") {
      console.warn("No valid handler found for type:", type);
      return;
    }

    // Safe process
    let processed;
    try {
      processed = handler(item);
    } catch (err) {
      console.error("Handler failed for item:", item, err);
      return;
    }

    if (processed) result.push(processed);
  });


  return result.sort((a, b) => a.section_order - b.section_order);
};


export const monthToName = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

export const OpenLink = (data) => {
  const pattern = /^((http|https|ftp):\/\/)/;
  if (!pattern.test(data)) {
    let url = "https://" + data;
    window.open(url, "_blank");
  } else {
    window.open(data, "_blank");
  }
};

export const getFormData = (files) => {
  let formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    formData.append("file", file, file.name);
  }
  return formData;
};

export const getFileUploads = (values) => {
  let filteredUploads = [];
  Object.keys(values ? values : {})?.forEach((key) => {
    if (values[key]?.length && Array.isArray(values[key])) {
      values[key]?.map((item, index) => {
        if (item?.preview || (item?.size && item?.type)) {
          let fileExt = item?.name?.split(".")?.pop();
          filteredUploads.push({
            file: item,
            name: `${key}_${index}.${fileExt}`,
          });
        }
        else if (item?.uploadUrl && Array.isArray(item?.uploadUrl) && item?.uploadUrl?.length) {
          item?.uploadUrl?.forEach((itemKey, ind) => {
            if (itemKey?.preview || (itemKey?.size && itemKey?.type)) {
              // let fileExt = itemKey?.name?.split(".")?.pop();
              filteredUploads.push({
                file: itemKey,
                name: `${key}|${index}_${ind}|${itemKey?.name?.replaceAll("_", "-")}`,
              });
            }
            else if (itemKey?.url?.preview || (itemKey?.url?.size && itemKey?.url?.type)) {
              let fileExt = itemKey?.url?.name?.split(".")?.pop();
              filteredUploads.push({
                file: itemKey?.url,
                name: `${key}|${index}_${ind}|${itemKey?.name?.replaceAll("_", "-")}`,
              });
            }
          })
        }
        else {
          Object.keys(item)?.forEach((itemKey) => {
            if (item[itemKey] && Array.isArray(item[itemKey]) && item[itemKey]?.length) {
              item[itemKey]?.forEach((subItem, subIndex) => {
                if (subItem?.preview || (subItem?.size && subItem?.type)) {
                  let fileExt = subItem?.name?.split(".")?.pop();
                  filteredUploads.push({
                    file: subItem,
                    name: `${key}|${itemKey}_${index}_${index}.${fileExt}`,
                  });
                }
              })
            }
            else if (item[itemKey]?.preview || (item[itemKey]?.size && item[itemKey]?.type)) {
              let fileExt = item[itemKey]?.name?.split(".")?.pop();
              filteredUploads.push({
                file: item[itemKey],
                name: `${key}|${itemKey}_${index}_${index}.${fileExt}`,
              });
            }
          });
        }
      });
    } else if (values[key]?.preview) {
      let fileExt = values[key]?.name?.split(".")?.pop();
      filteredUploads.push({
        file: values[key],
        name: `${key}_${0}.${fileExt}`,
      });
    } else if (values[key]?.uploadUrl) {
      if (values[key]?.uploadUrl?.preview || (values[key]?.uploadUrl?.size && values[key]?.uploadUrl?.type)) {
        filteredUploads.push({
          file: values[key].uploadUrl,
          name: `${key}_${0}|${values[key]?.uploadUrl?.url?.name?.replaceAll("_", "-")}`,
        });
      }
      else if (values[key]?.uploadUrl?.url?.preview || (values[key]?.uploadUrl?.url?.size && values[key]?.uploadUrl?.url?.type)) {
        filteredUploads.push({
          file: values[key].uploadUrl?.url,
          name: `${key}_${0}|${values[key]?.uploadUrl?.url?.name?.replaceAll("_", "-")}`,
        });
      }
    } else if (values[key]?.type && values[key]?.size && !values[key]?.preview) {
      let fileExt = values[key]?.name ? values[key]?.name?.split(".")?.pop() : null;
      filteredUploads.push({
        file: values[key],
        name: `${key}_${0}.${fileExt ? fileExt : "png"}`,
      });
    }
  });
  return filteredUploads;
};

export const getValuesWithFileUploads = (
  fileUploadData,
  schemaArray,
  formValues
) => {
  const newfileUploadData = (
    Array.isArray(fileUploadData) ? fileUploadData : [fileUploadData]
  )?.map((item) => {
    return Object.assign({}, item, {
      filename: item?.filename
        ?.split("_", item?.filename?.split("_")?.length - 1)
        ?.join("_"),
    });
  });
  let fileByType = _.groupBy(newfileUploadData, (x) => x.filename);
  let finalUploadData = {};
  Object.keys(fileByType)?.forEach((key) => {
    const [profileKey, profileFieldKey] = key?.split("|");
    const filteredSchema = schemaArray?.filter(
      (item) => item?.key === (profileFieldKey ? profileKey : key)
    )?.[0];
    if (filteredSchema && filteredSchema?.multiple) {
      if (filteredSchema?.type === "multiSelectableMultiUpload") {
        let fileArray = fileByType[key]
        fileArray?.forEach(fileObj => {
          const nameSplit = fileObj?.name?.split("|")
          let [parentIndex, childIndex] = nameSplit?.[nameSplit?.length - 2]?.split("_")
          if (formValues && formValues[filteredSchema.key] && formValues[filteredSchema.key][parentIndex] && formValues[filteredSchema.key][parentIndex].uploadUrl && formValues[filteredSchema.key][parentIndex].uploadUrl[childIndex]) {
            formValues[filteredSchema.key][parentIndex].uploadUrl[childIndex] = { url: fileObj?.publicUrl, name: nameSplit?.[nameSplit?.length - 1] }
          }
        })
      }
      else {
        const previousUploads = formValues?.[key]?.filter((img) => {
          if (img?.preview || img?.size || img?.type) {
            return false
          }
          else {
            return true
          }
        });
        finalUploadData = Object.assign({}, finalUploadData, {
          [key]: fileByType[key]
            ?.map((file) => file?.publicUrl)
            ?.concat(previousUploads),
        });
      }
    } else {
      if (filteredSchema?.type === "selectableUpload") {
        formValues[filteredSchema.key].uploadUrl = fileByType[key]?.map(
          (file) => {
            const actualFileName = file?.name?.split("|")
            return { url: file?.publicUrl, name: actualFileName?.[actualFileName?.length - 1] }
          }
        )?.[0];
      }
      else if (filteredSchema?.type === "multiSelectableMultiUpload") {
        let fileArray = fileByType[key]
        fileArray?.forEach(fileObj => {
          const nameSplit = fileObj?.name?.split("|")
          let [parentIndex, childIndex] = nameSplit?.[nameSplit?.length - 2]?.split("_")
          if (formValues && formValues[filteredSchema.key] && formValues[filteredSchema.key][parentIndex] && formValues[filteredSchema.key][parentIndex].uploadUrl && formValues[filteredSchema.key][parentIndex].uploadUrl[childIndex]) {
            formValues[filteredSchema.key][parentIndex].uploadUrl[childIndex] = { url: fileObj?.publicUrl, name: nameSplit?.[nameSplit?.length - 1] }
          }
        })
      } else if (filteredSchema?.type === "profile") {
        // At this point, profileFieldKey looks like:
        // "headshot_images_4"
        //
        // nestedFieldKey = "headshot_images"
        // profileIndex = "4"
        const [, nestedFieldKey, profileIndex] =
          profileFieldKey?.match(/^(.*)_(\d+)$/) || [];

        const profile = formValues?.[profileKey]?.[Number(profileIndex)];

        if (profile && nestedFieldKey) {
          profile[nestedFieldKey] = fileByType[key]
            ?.map((profileFile) => profileFile?.publicUrl)
            ?.filter(Boolean);
        }
      }
      else if (filteredSchema?.type === "checkList") {
        let lastIndex = profileFieldKey.lastIndexOf("_");
        let prefix = profileFieldKey.substring(0, lastIndex);
        let parentIndex = profileFieldKey.substring(lastIndex + 1);
        if (formValues && formValues[profileKey] && formValues[profileKey][parentIndex] && formValues[profileKey][parentIndex][prefix]) {
          formValues[profileKey][parentIndex][prefix] = fileByType[key]?.map((profileFile) => profileFile?.publicUrl) || [];
        };
      } else {
        finalUploadData = Object.assign({}, finalUploadData, {
          [key]: fileByType[key]?.map((file) => file?.publicUrl)?.[0],
        });
      }
    }
  });
  let finalValues = Object.assign({}, formValues, finalUploadData);
  Object.keys(finalValues)?.forEach((key) => {
    if (finalValues[key] === "") {
      delete finalValues[key];
    }
  });
  return finalValues;
};

export const dynamicFilter = (
  list,
  filterStatus,
  statusField,
  filterObj,
  parentTagId,
  module,
  viewMode,
  listTableObj,
  statusSchemaField,
  favoriteGoals,
  isProfile
) => {
  let actionStatusKeys = statusSchemaField?.options?.filter((opt) => opt?.action_status)?.map(x => x?.key);
  let finalProducts = list?.filter((item) => {
    return (parentTagId
      ? item?.parent_goal_id?.includes(parentTagId) ||
      (module !== "client" ? item?.[`${module}_ids`]?.includes(parentTagId) : true) ||
      (module !== "vendor" ? item?.[`${module}_ids`]?.includes(parentTagId) : true) ||
      (module !== "location" ? item?.[`${module}_ids`]?.includes(parentTagId) : true)
      : true)
  })
    ?.map((item) => item);
  if (Object.keys(filterObj)?.length) {
    Object.keys(filterObj)?.map((key) => {
      const [type, schemaKey] = key.split("|");
      finalProducts = finalProducts?.filter((product) => {
        if (type === "search") {
          if ((module === "goal" || module == "location" || module == "client") && !viewMode) {
            return true
          }
          else {
            let fieldTitle = isProfile ? `${product?.first_name}${product?.last_name ? product?.last_name : ""}` : product?.[schemaKey];
            return fieldTitle
              ?.toLowerCase()
              ?.includes(filterObj?.[key]?.toLowerCase());
          }
        }
        else if (type === "vendor") {
          const vendorTitles = product?.[`${schemaKey}`]?.map(opt => opt?.title)?.filter((opt) => opt?.toLowerCase()?.includes(filterObj?.[key]?.toLowerCase()))
          return filterObj?.[key]?.toLowerCase() ? vendorTitles && vendorTitles?.length > 0 : true
        } else if (type === "location") {
          if (!Array.isArray(filterObj?.[key]) && typeof filterObj?.[key] === "string") {
            const locationTitles = product?.[`${schemaKey}`]?.map(opt => opt?.title)?.filter((opt) => opt?.toLowerCase()?.includes(filterObj?.[key]?.toLowerCase()))
            return filterObj?.[key]?.toLowerCase() ? locationTitles && locationTitles?.length > 0 : true
          }
          else {
            const productLocationIds = product?.[`${schemaKey}`] ? product?.[`${schemaKey}`]?.map((item) => item?._id) : [];
            const filterLocationIds = filterObj?.[key]?.map((item) => item?._id)
            return filterObj[key] && filterObj[key]?.length
              ? _.intersection(productLocationIds, filterLocationIds)
                ?.length > 0
              : true;
          }
        } else if (type === "locationByField") {
          const locationVal = product?.[`${schemaKey}`]?.map(opt => opt?.value)
          if (!Array.isArray(filterObj?.[key]) && typeof filterObj?.[key] === "string") {
            const locationTitles = product?.[`${schemaKey}`]?.map(opt => opt?.value)?.filter((opt) => opt?.toLowerCase()?.includes(filterObj?.[key]?.toLowerCase()))
            return filterObj?.[key]?.toLowerCase() ? locationTitles && locationTitles?.length > 0 : true
          }
          else {
            const filterLocationVals = filterObj?.[key]?.map((item) => item?.value)
            return filterObj[key] && filterObj[key]?.length
              ? _.intersection(locationVal, filterLocationVals)
                ?.length > 0
              : true;
          }
        } else if (type === "client") {
          if (!Array.isArray(filterObj?.[key]) && typeof filterObj?.[key] === "string") {
            const clientTitles = product?.[`${schemaKey}`]?.map(opt => opt?.title)?.filter((opt) => opt?.toLowerCase()?.includes(filterObj?.[key]?.toLowerCase()))
            return filterObj?.[key]?.toLowerCase() ? clientTitles && clientTitles?.length > 0 : true
          }
          else {
            const productClientIds = product?.[`${schemaKey}`] ? product?.[`${schemaKey}`]?.map((item) => item?._id) : [];
            const filterClientIds = filterObj?.[key]?.map((item) => item?._id)
            return filterObj[key] && filterObj[key]?.length
              ? _.intersection(productClientIds, filterClientIds)
                ?.length > 0
              : true;
          }
        }
        else if (type === "profile") {
          const productProfileIds = product?.[`${schemaKey}`]?.map((item) => item?._id)
          const filterProfileIds = filterObj?.[key]?.map((item) => item?._id)
          return filterObj[key] && filterObj[key]?.length
            ? _.intersection(productProfileIds, filterProfileIds)
              ?.length > 0
            : true;
        }
        else if (type === "createdDetails") {
          const filterProfileNames = filterObj?.[key]?.map((item) => item?.first_name + (item?.last_name ? (" " + item?.last_name) : ""))
          if (filterProfileNames?.length > 0) {
            return filterProfileNames?.filter(proName => product?.["createdByName"]?.includes(proName))?.length
          } else {
            return true;
          }
        } else if (type === "input") {
          return product?.[schemaKey]
            ?.toLowerCase()
            ?.includes(filterObj?.[key]?.toLowerCase());
        } else if (type === "textArea") {
          return Boolean(filterObj?.[key]) ? product?.[schemaKey]?.toLowerCase()?.includes(filterObj?.[key]?.toLowerCase()) : true

        } else if (type === "number") {
          return filterObj?.[key] ? product?.[schemaKey]?.toString()?.includes(filterObj?.[key]) : true;
        } else if (type === "date" && schemaKey === "lastUpdatedOn") {
          let lastUpdatedStartDate = new Date(filterObj?.[`date|lastUpdatedOn`]?.start);
          let lastUpdatedEndDate = new Date(filterObj?.[`date|lastUpdatedOn`]?.end);
          let goalLastUpdatedDate = new Date(product?.updated_at);
          if (
            lastUpdatedStartDate &&
            lastUpdatedEndDate &&
            moment(lastUpdatedStartDate).isSameOrBefore(
              moment(lastUpdatedEndDate)
            )
          ) {
            lastUpdatedStartDate.setHours(0, 0, 0, 0);
            lastUpdatedEndDate.setHours(0, 0, 0, 0);
            goalLastUpdatedDate.setHours(0, 0, 0, 0);
            return (
              moment(lastUpdatedStartDate).isSameOrBefore(
                moment(goalLastUpdatedDate)
              ) &&
              moment(lastUpdatedEndDate).isSameOrAfter(
                moment(goalLastUpdatedDate)
              )
            );
          }
        }
        else if (type === "date") {
          if (
            filterObj[key]?.startDate &&
            filterObj[key]?.endDate &&
            moment(filterObj[key]?.startDate).isSameOrBefore(
              moment(filterObj[key]?.endDate)
            )
          ) {
            let startDate = new Date(filterObj[key]?.startDate);
            let endDate = new Date(filterObj[key]?.endDate);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            if (schemaKey === "startEndDate") {
              let productStartDate = new Date(product?.start_date);
              let productEndDate = new Date(product?.end_date);
              productStartDate.setHours(0, 0, 0, 0);
              productEndDate.setHours(0, 0, 0, 0);
              return (
                moment(startDate).isSameOrBefore(
                  moment(productStartDate)
                ) &&
                moment(endDate).isSameOrAfter(
                  moment(productEndDate)
                )
              );
            }
            let productDate = new Date(product?.[schemaKey]);
            productDate.setHours(0, 0, 0, 0);
            return (
              moment(startDate).isSameOrBefore(
                moment(productDate)
              ) &&
              moment(endDate).isSameOrAfter(
                moment(productDate)
              )
            );
          } else {
            if (schemaKey === "startEndDate") {
              return (
                (filterObj[key]?.startDate
                  ? moment(filterObj[key]?.startDate).isSameOrBefore(
                    moment(product?.start_date)
                  )
                  : true) &&
                (filterObj[key]?.endDate
                  ? moment(filterObj[key]?.endDate).isSameOrAfter(
                    moment(product?.end_date)
                  )
                  : true)
              );
            }
          }
          return true;
        } else if (type === "dateTime") {
          if (
            filterObj[key]?.startDate &&
            filterObj[key]?.endDate &&
            moment(filterObj[key]?.startDate).isSameOrBefore(
              moment(filterObj[key]?.endDate)
            )
          ) {
            return (
              moment(filterObj[key]?.startDate).isSameOrBefore(
                moment(product?.[schemaKey])
              ) &&
              moment(filterObj[key]?.endDate).isSameOrAfter(
                moment(product?.[schemaKey])
              )
            );
          }
          return true;
        } else if (type === "securityDeposit") {
          let listSDReturnDates = Array.isArray(product?.[schemaKey])
            ? product[schemaKey].map((x) => x?.return_date)
            : [];

          let flag = false;
          if (!filterObj[key]?.return_date) flag = true;
          if (listSDReturnDates?.length > 0) {
            flag = listSDReturnDates?.some((d) => {
              return moment(d).isSameOrBefore(filterObj[key]?.return_date)
            })
          }
          return flag;
        } else if (type === "select") {
          return filterObj[key] && filterObj[key]?.length
            ? filterObj[key]?.includes(product?.[schemaKey])
            : true;
        } else if (type === "boolean") {
          if (filterObj[key] === null || filterObj[key]?.id === null) {
            return true
          }
          // return product?.[schemaKey] === filterObj[key]?.id;
          // return filterObj[key]?.id ? product?.[schemaKey] === filterObj[key]?.id : product?.[schemaKey] === filterObj[key]?.id || product?.[schemaKey] === undefined;
          return filterObj[key]?.id ? product?.[schemaKey] === filterObj[key]?.id : product?.[schemaKey] === undefined;

        } else if (type === "favorite") {
          const localFavIdList = Object.keys(favoriteGoals ? favoriteGoals : {})?.filter(x => favoriteGoals?.[x]);
          return filterObj[key] ? localFavIdList?.includes(product?._id) : true;
        } else if (type === "multiSelectGoal") {
          return filterObj[key] && filterObj[key]?.length
            ? _.intersection(product?.["parent_goal_id"], filterObj[key])
              ?.length > 0
            : true;
        } else if (type === "multiSelectChildGoal") {
          const childids = product?.[schemaKey]?.map(val => val?.id)
          return filterObj[key] && filterObj[key]?.length
            ? _.intersection(childids, filterObj[key])
              ?.length > 0
            : true;
        } else if (type === "items") {
          const childids = product?.[schemaKey]?.map(val => val?.item_key);
          return filterObj[key] && filterObj[key]?.length
            ? _.intersection(childids, filterObj[key])
              ?.length > 0
            : true
        } else if (type === "status") {
          const arrayOfOptionKeys = (filterObj[key] && filterObj[key]?.length ? filterObj[key] : [])?.map((opt) => opt?.key);
          return filterObj[key] && filterObj[key]?.length ? arrayOfOptionKeys?.includes(product?.[schemaKey]) : true;
        }
        else if (type === "profileTag" || type === "typableMultiSelectText") {
          const collectTagsByKey = (obj, targetKey) => {
            let result = [];

            const search = (item) => {
              if (Array.isArray(item)) {
                item.forEach(search);
              } else if (typeof item === 'object' && item !== null) {
                for (const key in item) {
                  if (key === targetKey) {
                    result = result.concat(item[key]);
                  }
                  search(item[key]);
                }
              }
            };

            search(obj);
            return result;
          };

          // Collecting filter tags here
          const filterTags = filterObj?.[key] || [];

          if (type === "profileTag") {
            const finalTarget = collectTagsByKey(product, '_profile_client_tags');
            const clientTags = product?._client_tags || [];

            const hasMatchingProfileTags = _.intersection(finalTarget, filterTags).length > 0;
            const hasMatchingClientTags = _.intersection(clientTags, filterTags).length > 0;

            return filterTags.length ? (hasMatchingProfileTags || hasMatchingClientTags) : true;

          } else if (type === "typableMultiSelectText") {
            // Collect _profile_client_tags as well for this type
            const profileTags = collectTagsByKey(product, '_profile_client_tags');
            const finalTarget = product?.[schemaKey] || [];

            const lowerCaseProductTags = finalTarget.map(tag => tag.toLowerCase());
            const lowerCaseProfileTags = profileTags.map(tag => tag.toLowerCase());
            const lowerCaseFilterTags = filterTags.map(tag => tag.toLowerCase());

            const hasMatchingTags = _.intersection(lowerCaseProductTags, lowerCaseFilterTags).length > 0;
            const hasMatchingProfileTags = _.intersection(lowerCaseProfileTags, lowerCaseFilterTags).length > 0;

            return filterTags.length ? (hasMatchingTags || hasMatchingProfileTags) : true;
          }
        }


        else if (type === "typableSelectText") {
          // console.log(filterObj,"filterObj")
          return filterObj[key]
            ? filterObj[key] === product?.[schemaKey]
            : true;
        } else if (type === "multiSelect") {
          return filterObj[key] && filterObj[key]?.length
            ? _.intersection(product?.["product_ids"], filterObj[key])?.length >
            0 ||
            _.intersection(product?.["event_ids"], filterObj[key])?.length >
            0
            : true;
        }
      });
    });
  }
  let finalProductsWithoutTabFilter = finalProducts
  if (filterStatus && filterStatus !== "All") {
    finalProducts = finalProducts?.filter((item) => {
      return (
        item[statusField?.key] ===
        statusField?.options?.filter((opt) => opt?.name === filterStatus)?.[0]
          ?.key
      );
    });
  }
  else if (filterStatus === "All") {
    finalProducts = finalProducts?.filter((item) => {
      const isHidden = actionStatusKeys?.includes(item?.[statusSchemaField?.key]);
      return !isHidden
    })
  }

  if (listTableObj && Object.keys(listTableObj)?.length) {
    const filteredProductIds = finalProducts?.map((item) => item?._id);
    let finalObj = {};
    Object.keys(listTableObj)?.forEach((key) => {
      if (filteredProductIds?.includes(key)) {
        finalObj = Object.assign({}, finalObj, {
          [key]: listTableObj[key],
        });
      }
    });
    return {
      finalArray: finalProducts,
      finalTableObj: finalObj,
      finalProductsWithoutTabFilter
    };
  }
  return finalProducts;
};

export const applyFilter = ({ inputData, comparator, orderBy }) => {
  const stabilizedThis = inputData?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    let order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis?.map((el) => el[0]);
  return inputData;
}

export const applyFilterCheckbox = ({ inputData, comparator, orderBy, schema, order }) => {
  let filterType = schema?.filter(x => x?.key === orderBy)?.[0]?.type
  let tempArray = []
  let UnenteredDateArray = []

  if (filterType === "date") {
    tempArray = inputData?.length > 0 ? inputData?.filter(x => x?.[orderBy]) : []
    UnenteredDateArray = inputData?.length > 0 ? inputData?.filter(x => !x?.[orderBy]) : []
  } else {
    tempArray = inputData?.length > 0 ? inputData : []
    UnenteredDateArray = []
  }

  const stabilizedThis = tempArray?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    if (filterType === "boolean") {
      return order === "asc" ? a[0] ? -1 : 1 : a[0] ? 1 : -1
    } else {
      let order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    }
  });

  inputData = stabilizedThis?.map((el) => el[0]);
  return [...(inputData?.length ? inputData : []), ...(UnenteredDateArray?.length ? UnenteredDateArray : [])];
}

export const applySortFilter = ({
  tableLabels,
  tableData,
  comparator,
  moduleName,
}) => {
  let data = [];
  Object.keys(tableData).forEach((el, index) => {
    let temp = {};
    tableLabels?.forEach((header) => {
      if (header.id === moduleName) {
        const filteredData = tableData[el]?.filter(
          (item) => item?.key === "title"
        )?.[0];
        temp = Object.assign({}, temp, {
          [header.id]:
            typeof filteredData?.value === "string"
              ? filteredData?.value.trim().toLowerCase()
              : filteredData?.value,
        });
      } else {
        const filteredData = tableData[el]?.filter(
          (item) => item?.key === header?.id
        )?.[0];
        if (filteredData?.type === "money") {
          temp = Object.assign({}, temp, {
            [header.id]: filteredData?.value?.amount
              ? filteredData?.value?.amount
              : 0,
          });
        }
        else if (
          filteredData?.type === "date" ||
          filteredData?.type === "dateTime"
        ) {
          const isValidValue = typeof filteredData?.value === "string" || filteredData?.value instanceof String;
          temp = Object.assign({}, temp, {
            [header.id]: (isValidValue && filteredData?.value) ? moment(filteredData.value) : typeof filteredData?.value === "number" ? new Date(filteredData.value) : "N/A",
          });
        }
        else if (filteredData?.type === "multiSelectGoal") {
          temp = Object.assign({}, temp, {
            // [header.id]: filteredData?.value ? filteredData?.value?.length : 0,
            [header.id]: filteredData?.value?.length > 0 ? filteredData?.value[0]?.title : "",
          });
        }
        else if (filteredData?.type === "array") {
          temp = Object.assign({}, temp, {
            [header.id]: filteredData?.value
              ? filteredData?.value?.length
              : 0,
          });
        }
        else if (filteredData?.type === "typableMultiSelectText") {
          temp = Object.assign({}, temp, {
            [header.id]: filteredData?.value
              ? filteredData?.value?.length
              : 0,
          });
        }
        else if (filteredData?.type === "multiSelect") {
          temp = Object.assign({}, temp, {
            [header.id]: filteredData?.value ? filteredData?.value?.length : 0,
          });
        }
        else if (filteredData?.type === "profile") {
          temp = Object.assign({}, temp, {
            [header.id]:
              filteredData?.value?.[0]?.first_name &&
                filteredData?.value?.[0]?.last_name
                ? (
                  filteredData?.value?.[0]?.first_name +
                  " " +
                  filteredData?.value?.[0]?.last_name
                )
                  .trim()
                  .toLowerCase()
                : 0,
          });
        } else {
          temp = Object.assign({}, temp, {
            [header.id]:
              typeof filteredData?.value === "string"
                ? filteredData?.value.trim().toLowerCase()
                : filteredData?.value ? filteredData?.value : "",
          });
        }
      }
    });
    temp = Object.assign({}, temp, {
      module_id: el,
    });
    data.push(temp);
  });

  const stabilizedThis = data.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let finalData = {};
  stabilizedThis?.forEach((item) => {
    finalData = Object.assign({}, finalData, {
      [item[0]["module_id"]]: tableData[item[0]["module_id"]],
    });
  });

  return finalData;
};

export const getIconfromAllItems = (items) => {
  let finalIcon = "ic:round-account-box";
  if (items && items?.length) {
    items?.every((item) => {
      if (item?.section_icon) {
        finalIcon = item?.section_icon;
        return false;
      }
    });
  }
  return finalIcon;
};

export const getWindowSize = () => {
  const { innerWidth, innerHeight } = window;
  return { innerWidth, innerHeight };
};

export const getFieldValueByType = (fieldSchema, data, schema, productList) => {
  if (
    fieldSchema?.type === "input" ||
    fieldSchema?.type === "email" ||
    fieldSchema?.type === "phone" ||
    fieldSchema?.type === "url" ||
    fieldSchema?.type === "textArea" ||
    fieldSchema?.type === "typableSelectText"
  ) {
    return data?.[fieldSchema?.key] ? data?.[fieldSchema?.key] : "";
  } else if (
    fieldSchema?.type === "select" ||
    fieldSchema?.type === "radio" ||
    fieldSchema?.type === "checkbox" ||
    fieldSchema?.type === "status"
  ) {
    const options = (fieldSchema?.options ? fieldSchema?.options : [])?.filter(
      (item) => item?.key === data?.[fieldSchema?.key]
    )?.[0];
    return options ? options?.name : "";
  } else if (fieldSchema?.type === "boolean") {
    return data?.[fieldSchema?.key] ? "Yes" : "No";
  } else if (fieldSchema?.type === "date") {
    return data?.[fieldSchema?.key]
      ? moment(data?.[fieldSchema?.key]).format("MM-DD-YYYY")
      : "";
  } else if (fieldSchema?.type === "dateTime") {
    return data?.[fieldSchema?.key]
      ? moment(data?.[fieldSchema?.key]).format("MM-DD-YYYY HH:mm")
      : "";
  } else if (fieldSchema?.type === "money") {
    return data?.[fieldSchema?.key] && data?.[fieldSchema?.key]?.amount
      ? `$${data?.[fieldSchema?.key]?.amount}`
      : "";
  } else if (fieldSchema?.type === "array") {
    return data?.[fieldSchema?.key]
      ? data?.[fieldSchema?.key]?.filter((item) => item)?.join(", ")
      : "";
  } else if (fieldSchema?.type === "currency") {
    return data?.[fieldSchema?.key] ? `$${data?.[fieldSchema?.key]}` : "";
  } else if (fieldSchema?.type === "video") {
    return data?.[fieldSchema?.key]
      ? `${data?.[fieldSchema?.key]?.videoTitle}`
      : "";
  } else if (fieldSchema?.type === "selectableUpload") {
    const options = (fieldSchema?.options ? fieldSchema?.options : [])?.filter(
      (item) => item?.key === data?.[fieldSchema?.key]?.uploadType
    )?.[0];
    return options ? options?.name : "";
  } else if (fieldSchema?.type === "upload") {
    return fieldSchema?.multiple
      ? `${data?.[fieldSchema?.key]?.length} Files`
      : "1 File";
  } else if (fieldSchema?.type === "location") {
    const finalValue = data?.[fieldSchema?.key] && data?.[fieldSchema?.key]?.map((item) => fieldSchema?.location_fields?.map((field) => item?.[field?.key] && (typeof item?.[field?.key] === "string") ? item?.[field?.key] : "")?.filter(val => val)?.join(", "))
      ?.filter((item) => item)
      ?.join(" | ");
    return finalValue ? finalValue : "";
  } else if (fieldSchema?.type === "client") {
    const finalValue = data?.[fieldSchema?.key]?.map((item) => fieldSchema?.client_fields?.map((field) => item?.[field?.key] && (typeof item?.[field?.key] === "string") ? item?.[field?.key] : "")?.filter(val => val)?.join(", "))
      ?.filter((item) => item)
      ?.join(" | ");
    return finalValue ? finalValue : "";
  } else if (fieldSchema?.type === "multiSelect") {
    const finalValue = data?.["_products_data"]?.map((item) => item?.title)
      ?.filter((item) => item)
      ?.join(", ");
    return finalValue ? finalValue : "";
  } else if (fieldSchema?.type === "profile") {
    const finalValue = data?.[fieldSchema?.key]
      ?.map(
        (item) =>
          `${item?.last_name ? item?.last_name : item?.first_name ? item?.first_name : item?.email ? item?.email : ""}`
      )
      ?.filter((item) => item)
      ?.join(", ");
    return finalValue ? finalValue : "";
  }
  return "";
};

const hasDialogFieldValue = (field, value) => {
  if (field?.type === "boolean") {
    return typeof value === "boolean";
  }

  if (field?.type === "video") {
    return Boolean(value?.videoTitle && value?.videoAddress);
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null && value !== "";
};

export const validateNewProfile = (
  field,
  value,
  profileFields,
  dialogValue,
  setProfileDialogErrorObj,
  handleClose
) => {
  let finalValue = {}
  let noOfFieldsValid = 0;
  let erroObj = {};
  profileFields?.forEach((item) => {
    if (item?.required) {
      if (hasDialogFieldValue(item, dialogValue?.[item?.key])) {
        if (item?.type === "email") {
          const mailformat =
            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          if (dialogValue?.[item?.key]?.match(mailformat)) {
            noOfFieldsValid = noOfFieldsValid + 1;
          } else {
            erroObj = Object.assign({}, erroObj, {
              [item?.key]: "Please enter a valid email",
            });
          }
        }
        if (item?.type === "profile") {
          if (dialogValue?.[item?.key] && Object.keys(dialogValue?.[item?.key])?.length) {
            noOfFieldsValid = noOfFieldsValid + 1;
          } else {
            erroObj = Object.assign({}, erroObj, {
              [item?.key]: `Please select a ${field?.name}`,
            });
          }
        }
        if (item?.type === "client") {
          if (dialogValue?.[item?.key] && Object.keys(dialogValue?.[item?.key])?.length) {
            noOfFieldsValid = noOfFieldsValid + 1;
          } else {
            erroObj = Object.assign({}, erroObj, {
              [item?.key]: `Please select a ${field?.name}`,
            });
          }
        }
        if (item?.type === "phone") {
          if (
            /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/.test(
              dialogValue[item?.key]?.phone
            )
          ) {
            noOfFieldsValid = noOfFieldsValid + 1;
            finalValue[item.key] = (dialogValue[item?.key]?.countryCode ? (dialogValue[item?.key]?.countryCode + "-") : "") + (dialogValue[item?.key]?.phone ? dialogValue[item?.key]?.phone : "") + (dialogValue[item?.key]?.ext ? ("-" + dialogValue[item?.key]?.ext) : "")
          } else {
            erroObj = Object.assign({}, erroObj, {
              [item?.key]: "Please enter a valid Phone number",
            });
          }
        }
        if (item?.type === "date") {
          if (moment(dialogValue[item?.key], "MM/DD/YYYY", true).isValid()) {
            noOfFieldsValid = noOfFieldsValid + 1;
          } else {
            erroObj = Object.assign({}, erroObj, {
              [item?.key]: "Please enter a valid date",
            });
          }
        } else {
          noOfFieldsValid = noOfFieldsValid + 1;
        }
      } else {
        erroObj = Object.assign({}, erroObj, {
          [item?.key]: `Please enter ${item?.name}`,
        });
      }
    } else {
      if (hasDialogFieldValue(item, dialogValue?.[item?.key])) {
        if (item?.type === "email") {
          if (dialogValue?.[item?.key]) {
            const mailformat =
              /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            if (dialogValue?.[item?.key]?.match(mailformat)) {
              noOfFieldsValid = noOfFieldsValid + 1;
            } else {
              erroObj = Object.assign({}, erroObj, {
                [item?.key]: "Please enter a valid email",
              });
            }
          } else {
            noOfFieldsValid = noOfFieldsValid + 1;
          }
        }
        if (item?.type === "phone") {
          if (dialogValue[item?.key]?.phone) {
            if (
              /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/.test(
                dialogValue[item?.key]?.phone
              )
            ) {
              noOfFieldsValid = noOfFieldsValid + 1;
              finalValue[item.key] = (dialogValue[item?.key]?.countryCode ? (dialogValue[item?.key]?.countryCode + "-") : "") + (dialogValue[item?.key]?.phone ? dialogValue[item?.key]?.phone : "") + (dialogValue[item?.key]?.ext ? ("-" + dialogValue[item?.key]?.ext) : "")
            } else {
              erroObj = Object.assign({}, erroObj, {
                [item?.key]: "Please enter a valid Phone number",
              });
            }
          }
          else {
            noOfFieldsValid = noOfFieldsValid + 1;
            finalValue[item.key] = (dialogValue[item?.key]?.countryCode ? (dialogValue[item?.key]?.countryCode + "-") : "") + (dialogValue[item?.key]?.phone ? dialogValue[item?.key]?.phone : "") + (dialogValue[item?.key]?.ext ? ("-" + dialogValue[item?.key]?.ext) : "")
          }
        }
        if (item?.type === "date") {
          if (dialogValue[item?.key]) {
            if (moment(dialogValue[item?.key], "MM/DD/YYYY", true).isValid()) {
              noOfFieldsValid = noOfFieldsValid + 1;
            } else {
              erroObj = Object.assign({}, erroObj, {
                [item?.key]: "Please enter a valid date",
              });
            }
          }
          else {
            noOfFieldsValid = noOfFieldsValid + 1;
          }
        } else {
          noOfFieldsValid = noOfFieldsValid + 1;
        }
      }
    }
  });
  if (value[value?.length - 1].hasOwnProperty("profile_client_tags")) {
    delete value._profile_client_tags
  }
  const finalObj = Object.assign({}, value[value?.length - 1], finalValue)
  delete finalObj._profile_client_tags;
  value[value?.length - 1] = finalObj
  if (erroObj && Object.keys(erroObj)?.length) {
    setProfileDialogErrorObj(erroObj);
  } else {
    field.onChange(value);
    handleClose(finalObj);
  }
};

export function fShortenNumber(number) {
  const format = number ? numeral(number).format('0.00a') : '';

  return format;
}

export const invoiceStatus = {
  "Draft": "draft",
  "Published": "published",
  "Partially Paid": "partiallyPaid",
  "Paid": "paid",
  "Cancelled": "cancelled",
  "Partially Published": "partiallyPublished"
}

export const invoiceStatusLabels = {
  'draft': 'Draft',
  'published': 'Published',
  'cancelled': 'Cancelled',
  'paid': 'Paid',
  'refunded': 'Refunded',
  'partiallyPaid': 'Partially Paid',
  'partiallyPublished': 'Partially Published'
}

export const donationStatus = {
  "Draft": "draft",
  "Published": "published",
  "Subscribed": "subscribed",
  "Cancelled": "cancelled",
  "Paid": "paid",
  "Refunded": "refunded"
}
export const donationStatusLabels = {
  "draft": "Draft",
  "published": "Published",
  "subscribed": "Subscribed",
  "cancelled": "Cancelled",
  "paid": "Paid",
  "refunded": "Refunded"
}

export const donationRefundStatusLabels = {
  "not_refunded": "Not Refunded",
  "pending": "Pending",
  "partially_refunded": "Partially Refunded",
  "refunded": "Refunded",
  "failed": "Failed",
  "succeeded": "Succeeded",
  "canceled": "Canceled",
  "cancelled": "Canceled",
  "requires_action": "Requires Action"
}

export const vendorInvoiceStatus = {
  "Quotes": "draft",
  "Published": "published",
  "Partially Invoiced": "partiallyInvoiced",
  "Invoiced": "invoiced",
  "Partially Paid": "partiallyPaid",
  "Paid": "paid",
  "Cancelled": "cancelled",
}

export const vendorInvoiceStatusLabels = {
  'draft': 'Quotes',
  'published': 'Published',
  'cancelled': 'Cancelled',
  'paid': 'Paid',
  'partiallyInvoiced': 'Partially Invoiced',
  'invoiced': 'Invoiced',
  'partiallyPaid': 'Partially Paid'
}

export const checkEditAllowed = (emailBurstData) => {
  if (emailBurstData && emailBurstData?._id) {
    if (emailBurstData?.send_details?.type === "scheduled") {
      if (["draft", "ready", "inProgress", "failed"].includes(emailBurstData?.status)) {
        return true
      }
      else if (emailBurstData?.status === "completed") {
        return false
      }
    }
    else {
      if (["draft", "failed"].includes(emailBurstData?.status)) {
        return true
      }
      else {
        return false
      }
    }
    return false
  }
  return true
}

export const handleNavigate = (selectedNotification, navigate, profileType) => {
  if (selectedNotification?.module_type === "goal") {
    navigate(profileType !== "admin" ? `/me/goals/tag/${selectedNotification?.tag}/view/${selectedNotification?.module_id}` : `/goals/tag/${selectedNotification?.tag}/view/${selectedNotification?.module_id}`)
  }
  else if (selectedNotification?.module_type === "product") {
    navigate(profileType !== "admin" ? `/me/products/view/${selectedNotification?.module_id}` : '/products/view/${selectedNotification?.module_id}')
  }
  else if (selectedNotification?.module_type === "contract") {
    navigate(profileType !== "admin" ? `/me/contract/${selectedNotification?.module_id}` : `/contract/${selectedNotification?.module_id}`)
  }
  else if (selectedNotification?.module_type === "client") {
    navigate(profileType !== "admin" ? `/me/clients/view/${selectedNotification?.module_id}` : `/clients/view/${selectedNotification?.module_id}`)
  }
  else if (selectedNotification?.module_type === "vendor") {
    navigate(profileType !== "admin" ? `/me/vendors/view/${selectedNotification?.module_id}` : `/vendors/view/${selectedNotification?.module_id}`)
  }
  else if (selectedNotification?.module_type === "invoice") {
    navigate(profileType !== "admin" ? `/me/goal/${selectedNotification?.goal_id}/tag/${selectedNotification?.tag_id}/invoice/${selectedNotification?.module_id}` : `goal/${selectedNotification?.goal_id}/tag/${selectedNotification?.tag_id}/invoice/${selectedNotification?.module_id}`)
  }
  else {
    navigate('/')
  }
};

const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}
export const replaceAll = (str, find, replace) => {
  return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
}

export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export const isOverlappingDates = (start1, end1, start2, end2) => {
  return (start1 < end2 && start2 < end1);
}

export const isHTML = (str) => {
  var doc = new DOMParser().parseFromString(str, "text/html");
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
}

export const dataURLtoFile = (dataurl, filename) => {
  var arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[arr.length - 1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export const isMidnight = (date) => {
  return date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0; // Include milliseconds for strict comparison
}

export const getSpanDataIds = (htmlString) => {
  // 1. Create a new DOMParser instance.
  const parser = new DOMParser();

  // 2. Parse the HTML string into a temporary HTML document.
  const doc = parser.parseFromString(htmlString, 'text/html');

  // 3. Select all <span> elements that have a 'data-id' attribute.
  const spansWithDataId = doc.querySelectorAll('span[data-id]');

  // 4. Convert the NodeList to an array and map over it to extract the 'data-id' value from each span.
  // We use `dataset.id` which is the modern way to access data-* attributes.
  const ids = Array.from(spansWithDataId).map(span => span.dataset.id);

  return ids;
}
